// ========= Copyright 2025-2026 @ Hanggent.AI All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Hanggent.AI All Rights Reserved. =========

import {
  proxyFetchDelete,
  proxyFetchGet,
  proxyFetchPost,
  proxyFetchPut,
} from '@/api/http';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NoteData {
  id: number;
  user_id: number;
  project_id: string | null;
  task_id: string | null;
  name: string;
  content: string;
  source: 'agent' | 'user';
  agent_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface NoteCreatePayload {
  name: string;
  content?: string;
  project_id?: string;
  task_id?: string;
  source?: 'agent' | 'user';
  agent_name?: string;
}

export interface NoteUpdatePayload {
  name?: string;
  content?: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** List notes with optional filters. */
export const fetchNotes = async (
  projectId?: string,
  taskId?: string,
  source?: 'agent' | 'user',
): Promise<NoteData[]> => {
  const params: Record<string, string> = {};
  if (projectId) params.project_id = projectId;
  if (taskId) params.task_id = taskId;
  if (source) params.source = source;
  return proxyFetchGet('/api/notes', params);
};

/** Get a single note. */
export const fetchNote = async (noteId: number): Promise<NoteData> => {
  return proxyFetchGet(`/api/notes/${noteId}`);
};

/** Create a new note. */
export const createNote = async (
  data: NoteCreatePayload,
): Promise<NoteData> => {
  return proxyFetchPost('/api/notes', data);
};

/** Update an existing note (partial). */
export const updateNote = async (
  noteId: number,
  data: NoteUpdatePayload,
): Promise<NoteData> => {
  return proxyFetchPut(`/api/notes/${noteId}`, data);
};

/** Append content to an existing note. */
export const appendNote = async (
  noteId: number,
  content: string,
): Promise<NoteData> => {
  return proxyFetchPost(`/api/notes/${noteId}/append`, { content });
};

/** Soft-delete a note. */
export const deleteNote = async (noteId: number): Promise<void> => {
  return proxyFetchDelete(`/api/notes/${noteId}`);
};
